/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {Role} from 'app/shared/Common/Enums'
import {HasPermissionInRole} from 'app/shared/Common/Util'
import React, {FC} from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import {App, NoAuthApp} from '../App'
import {AuthPage, Logout, useAuth} from '../modules/auth'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {PrivateRoutes} from './PrivateRoutes'
import {PrivacyPolicy} from 'app/modules/root-pages/privacy-policy'
import {TermsConditions} from 'app/modules/root-pages/terms-conditions'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth();
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              {HasPermissionInRole(currentUser, [Role.INT_Report_Full, Role.INT_Report_Read]) ? (
                <Route index element={<Navigate to='/dashboard' />} />
              ) : (
                <Route index element={<Navigate to='/admin/user-management' />} />
              )}
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' replace />} />
            </>
          )}
        </Route>
        <Route element={<NoAuthApp/>}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='terms-conditions' element={<TermsConditions />} />
          <Route path='privacy-policy' element={<PrivacyPolicy />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
